/**
 * Rapier 2D physics world wrapper for Warmancer space combat.
 * Provides a high-level API over @dimforge/rapier2d-compat for both
 * server (Durable Object) and client (browser prediction).
 *
 * Key design decisions:
 * - world.gravity = {x:0, y:0} (space) -- point-source gravity applied manually
 * - Rotation is game-controlled (setRotation + setAngvel(0) each tick)
 * - Moon is a KinematicPositionBased body, moved via setNextKinematicTranslation() for proper collision
 * - Boundary is 4 static cuboid colliders
 * - Uses applyImpulse(F*dt) instead of addForce(F) to eliminate the class of
 *   force-accumulation bugs (addForce is persistent across steps in Rapier 0.14)
 */
import RAPIER from '@dimforge/rapier2d-compat';
import { GRAVITY_CONSTANT, GRAVITY_RANGE_MULTIPLIER, TICK_DELTA, WORLD_HALF_SIZE, BOUNCE_RESTITUTION, FIGHTER_RADIUS, FIGHTER_MASS, SHIP_LINEAR_DAMPING, } from './constants.js';
// Collision group bits (membership in upper 16, filter in lower 16)
export const GROUP_SHIP = 0x0001;
export const GROUP_ASTEROID = 0x0002;
export const GROUP_CELESTIAL = 0x0004;
export const GROUP_BOUNDARY = 0x0008;
export const GROUP_PROJECTILE = 0x0010; // Future
const SHIP_FILTER = GROUP_SHIP | GROUP_ASTEROID | GROUP_CELESTIAL | GROUP_BOUNDARY;
const ASTEROID_FILTER = GROUP_SHIP | GROUP_ASTEROID | GROUP_CELESTIAL | GROUP_BOUNDARY;
const CELESTIAL_FILTER = GROUP_SHIP | GROUP_ASTEROID;
const BOUNDARY_FILTER = GROUP_SHIP | GROUP_ASTEROID;
function collisionGroups(membership, filter) {
    return (membership << 16) | filter;
}
let rapierInitialized = false;
/**
 * Initialize the Rapier WASM module. Must be called before creating RapierWorld.
 * Safe to call multiple times -- only initializes once.
 */
export async function initRapier() {
    if (rapierInitialized)
        return;
    await RAPIER.init();
    rapierInitialized = true;
}
const BOUNDARY_THICKNESS = 2.0;
/**
 * RapierWorld wraps a RAPIER.World and manages all rigid bodies/colliders
 * for one arena instance (server or client prediction).
 */
export class RapierWorld {
    world;
    shipBodies = new Map();
    celestialBodies = [];
    asteroidBodies = new Map();
    constructor() {
        this.world = new RAPIER.World({ x: 0, y: 0 });
        this.world.integrationParameters.dt = TICK_DELTA;
    }
    /**
     * Add 4 static cuboid colliders as arena boundaries.
     */
    addBoundaryWalls() {
        const hs = WORLD_HALF_SIZE;
        const t = BOUNDARY_THICKNESS;
        const halfT = t / 2;
        // Walls: position at edge + half-thickness offset
        const walls = [
            { x: hs + halfT, y: 0, hx: halfT, hy: hs + t }, // Right
            { x: -(hs + halfT), y: 0, hx: halfT, hy: hs + t }, // Left
            { x: 0, y: hs + halfT, hx: hs + t, hy: halfT }, // Top
            { x: 0, y: -(hs + halfT), hx: hs + t, hy: halfT }, // Bottom
        ];
        for (const wall of walls) {
            const bodyDesc = RAPIER.RigidBodyDesc.fixed().setTranslation(wall.x, wall.y);
            const body = this.world.createRigidBody(bodyDesc);
            const colliderDesc = RAPIER.ColliderDesc.cuboid(wall.hx, wall.hy)
                .setRestitution(BOUNCE_RESTITUTION)
                .setCollisionGroups(collisionGroups(GROUP_BOUNDARY, BOUNDARY_FILTER));
            this.world.createCollider(colliderDesc, body);
        }
    }
    /**
     * Add a celestial body (planet = static, moon = kinematic).
     */
    addCelestialBody(celestial, isKinematic) {
        const bodyDesc = isKinematic
            ? RAPIER.RigidBodyDesc.kinematicPositionBased().setTranslation(celestial.x, celestial.y)
            : RAPIER.RigidBodyDesc.fixed().setTranslation(celestial.x, celestial.y);
        const body = this.world.createRigidBody(bodyDesc);
        const colliderDesc = RAPIER.ColliderDesc.ball(celestial.radius)
            .setRestitution(BOUNCE_RESTITUTION)
            .setCollisionGroups(collisionGroups(GROUP_CELESTIAL, CELESTIAL_FILTER));
        this.world.createCollider(colliderDesc, body);
        this.celestialBodies.push({
            bodyHandle: body.handle,
            mass: celestial.mass,
            x: celestial.x,
            y: celestial.y,
            radius: celestial.radius,
        });
    }
    /**
     * Add a ship as a dynamic rigid body.
     * Uses collider density (not setAdditionalMass) to set mass, because
     * Rapier 0.14 density(0)+setAdditionalMass doesn't set internal mass
     * for force integration, causing impulse to produce infinite acceleration.
     */
    addShip(entityId, x, y, rotation) {
        const bodyDesc = RAPIER.RigidBodyDesc.dynamic()
            .setTranslation(x, y)
            .setRotation(rotation)
            .setLinearDamping(SHIP_LINEAR_DAMPING)
            .setAngularDamping(0)
            .setCcdEnabled(false)
            .lockRotations();
        const body = this.world.createRigidBody(bodyDesc);
        // density = mass / (π * r²) so Rapier computes correct mass for force integration
        const shipDensity = FIGHTER_MASS / (Math.PI * FIGHTER_RADIUS * FIGHTER_RADIUS);
        const colliderDesc = RAPIER.ColliderDesc.ball(FIGHTER_RADIUS)
            .setDensity(shipDensity)
            .setRestitution(BOUNCE_RESTITUTION)
            .setCollisionGroups(collisionGroups(GROUP_SHIP, SHIP_FILTER));
        this.world.createCollider(colliderDesc, body);
        this.shipBodies.set(entityId, { handle: body.handle, mass: FIGHTER_MASS });
    }
    /**
     * Add an asteroid as a dynamic rigid body.
     * Uses collider density to set mass (same fix as addShip - see comment there).
     */
    addAsteroid(x, y, radius, vx, vy, mass) {
        const bodyDesc = RAPIER.RigidBodyDesc.dynamic()
            .setTranslation(x, y)
            .setLinvel(vx, vy)
            .setLinearDamping(0)
            .setAngularDamping(0);
        const body = this.world.createRigidBody(bodyDesc);
        // density = mass / (π * r²) so Rapier computes correct mass for force integration
        const asteroidDensity = mass / (Math.PI * radius * radius);
        const colliderDesc = RAPIER.ColliderDesc.ball(radius)
            .setDensity(asteroidDensity)
            .setRestitution(BOUNCE_RESTITUTION)
            .setCollisionGroups(collisionGroups(GROUP_ASTEROID, ASTEROID_FILTER));
        this.world.createCollider(colliderDesc, body);
        this.asteroidBodies.set(body.handle, { radius, mass });
    }
    /**
     * Remove a ship from the world.
     */
    removeShip(entityId) {
        const entry = this.shipBodies.get(entityId);
        if (entry === undefined)
            return;
        const body = this.world.getRigidBody(entry.handle);
        if (body) {
            this.world.removeRigidBody(body);
        }
        this.shipBodies.delete(entityId);
    }
    /**
     * Apply point-source gravity from all celestial bodies to all dynamic bodies.
     * Uses impulse (F * dt) instead of persistent force to avoid accumulation bugs.
     * Physics equivalence: addForce(F) over dt → dv = (F/m)*dt.
     * applyImpulse(F*dt) → dv = (F*dt)/m = (F/m)*dt. Identical result, no cleanup needed.
     */
    applyGravity() {
        // Use stored celestial positions (ch.x/ch.y) instead of body.translation().
        // With setNextKinematicTranslation(), body.translation() returns the OLD position
        // until after step(). Stored positions are always current: updated immediately
        // by updateMoonOrbit() and resetMoonPosition(). Planet positions never change.
        const celestials = [];
        for (const ch of this.celestialBodies) {
            celestials.push({ x: ch.x, y: ch.y, mass: ch.mass, radius: ch.radius });
        }
        // Apply to ships
        for (const [, entry] of this.shipBodies) {
            const body = this.world.getRigidBody(entry.handle);
            if (!body)
                continue;
            const pos = body.translation();
            let fx = 0;
            let fy = 0;
            for (const cel of celestials) {
                const dx = cel.x - pos.x;
                const dy = cel.y - pos.y;
                const distSq = dx * dx + dy * dy;
                const dist = Math.sqrt(distSq);
                if (dist < cel.radius + FIGHTER_RADIUS)
                    continue; // At or inside collision surface, no gravity
                if (dist > cel.radius * GRAVITY_RANGE_MULTIPLIER)
                    continue; // Beyond gravity range
                const forceMag = (GRAVITY_CONSTANT * entry.mass * cel.mass) / dist;
                fx += (forceMag * dx) / dist;
                fy += (forceMag * dy) / dist;
            }
            body.applyImpulse({ x: fx * TICK_DELTA, y: fy * TICK_DELTA }, true);
        }
        // Apply to asteroids
        for (const [handle, meta] of this.asteroidBodies) {
            const body = this.world.getRigidBody(handle);
            if (!body)
                continue;
            const pos = body.translation();
            let fx = 0;
            let fy = 0;
            for (const cel of celestials) {
                const dx = cel.x - pos.x;
                const dy = cel.y - pos.y;
                const distSq = dx * dx + dy * dy;
                const dist = Math.sqrt(distSq);
                if (dist < cel.radius + meta.radius)
                    continue; // At or inside collision surface, no gravity
                if (dist > cel.radius * GRAVITY_RANGE_MULTIPLIER)
                    continue; // Beyond gravity range
                const forceMag = (GRAVITY_CONSTANT * meta.mass * cel.mass) / dist;
                fx += (forceMag * dx) / dist;
                fy += (forceMag * dy) / dist;
            }
            body.applyImpulse({ x: fx * TICK_DELTA, y: fy * TICK_DELTA }, true);
        }
    }
    /**
     * Apply an external force to a ship for this tick (e.g., thrust).
     * Internally converts force to impulse (F * dt) for one-shot application.
     */
    applyShipForce(entityId, force) {
        const entry = this.shipBodies.get(entityId);
        if (entry === undefined)
            return;
        const body = this.world.getRigidBody(entry.handle);
        if (!body)
            return;
        body.applyImpulse({ x: force.x * TICK_DELTA, y: force.y * TICK_DELTA }, true);
    }
    /**
     * Set ship rotation directly. Rapier does not simulate angular dynamics for ships --
     * rotation is game-controlled (turn rate from input).
     */
    setShipRotation(entityId, rotation) {
        const entry = this.shipBodies.get(entityId);
        if (entry === undefined)
            return;
        const body = this.world.getRigidBody(entry.handle);
        if (!body)
            return;
        body.setRotation(rotation, true);
        body.setAngvel(0, true);
    }
    /**
     * Schedule the kinematic moon body to move to a new position on the next step().
     * Uses setNextKinematicTranslation() so Rapier computes moon velocity =
     * (nextPos - currentPos) / dt, enabling proper kinematic-dynamic collision
     * response (moon pushes ships out of its path instead of teleporting through them).
     * Stored x/y are updated immediately for applyGravity() reads (body.translation()
     * still returns the old position until after step()).
     */
    updateMoonOrbit(moonX, moonY) {
        // The moon is always the second celestial body added (index 1)
        if (this.celestialBodies.length < 2)
            return;
        const moonHandle = this.celestialBodies[1];
        const body = this.world.getRigidBody(moonHandle.bodyHandle);
        if (!body)
            return;
        body.setNextKinematicTranslation({ x: moonX, y: moonY });
        // Update stored positions immediately for applyGravity() reads
        moonHandle.x = moonX;
        moonHandle.y = moonY;
    }
    /**
     * Reset the moon to an exact position (for reconciliation).
     * Uses setTranslation() for immediate position set — needed before reconciliation
     * replay so the first setNextKinematicTranslation() computes correct velocity
     * from serverTick_pos -> serverTick+1_pos.
     */
    resetMoonPosition(moonX, moonY) {
        if (this.celestialBodies.length < 2)
            return;
        const moonHandle = this.celestialBodies[1];
        const body = this.world.getRigidBody(moonHandle.bodyHandle);
        if (!body)
            return;
        body.setTranslation({ x: moonX, y: moonY }, true);
        moonHandle.x = moonX;
        moonHandle.y = moonY;
    }
    /**
     * Step the physics world forward by one tick.
     * No force cleanup needed — we use applyImpulse() which is one-shot per call.
     */
    step() {
        this.world.step();
    }
    /**
     * Extract ship state from Rapier body.
     */
    extractShipState(entityId) {
        const entry = this.shipBodies.get(entityId);
        if (entry === undefined)
            return null;
        const body = this.world.getRigidBody(entry.handle);
        if (!body)
            return null;
        const pos = body.translation();
        const vel = body.linvel();
        return {
            x: pos.x,
            y: pos.y,
            vx: vel.x,
            vy: vel.y,
            rotation: body.rotation(),
        };
    }
    /**
     * Extract all asteroid states from Rapier bodies.
     */
    extractAsteroidStates() {
        const result = [];
        for (const [handle, meta] of this.asteroidBodies) {
            const body = this.world.getRigidBody(handle);
            if (!body)
                continue;
            const pos = body.translation();
            const vel = body.linvel();
            result.push({
                x: pos.x,
                y: pos.y,
                vx: vel.x,
                vy: vel.y,
                radius: meta.radius,
                mass: meta.mass,
            });
        }
        return result;
    }
    /**
     * Set an asteroid's position and velocity by index (insertion order).
     * Used for respawning asteroids that collide with the planet.
     */
    setAsteroidState(index, x, y, vx, vy) {
        let i = 0;
        for (const [handle] of this.asteroidBodies) {
            if (i === index) {
                const body = this.world.getRigidBody(handle);
                if (body) {
                    body.setTranslation({ x, y }, true);
                    body.setLinvel({ x: vx, y: vy }, true);
                }
                return;
            }
            i++;
        }
    }
    /**
     * Snapshot all asteroid body states (position + velocity).
     * Used to save/restore around reconciliation so extra physics steps
     * don't cause asteroid drift.
     */
    snapshotAsteroidStates() {
        const result = [];
        for (const [handle] of this.asteroidBodies) {
            const body = this.world.getRigidBody(handle);
            if (!body)
                continue;
            const pos = body.translation();
            const vel = body.linvel();
            result.push({ handle, x: pos.x, y: pos.y, vx: vel.x, vy: vel.y });
        }
        return result;
    }
    /**
     * Restore asteroid body states from a snapshot.
     */
    restoreAsteroidStates(snapshot) {
        for (const s of snapshot) {
            const body = this.world.getRigidBody(s.handle);
            if (!body)
                continue;
            body.setTranslation({ x: s.x, y: s.y }, true);
            body.setLinvel({ x: s.vx, y: s.vy }, true);
        }
    }
    /**
     * Depenetrate a ship from all overlapping asteroids.
     * Pushes the ship out along the separation normal (position-only correction).
     * Velocity is NOT modified — Rapier handles collision response naturally on the next step.
     * Used after reconciliation to fix overlaps caused by prediction error and
     * asteroid restore shifting relative positions.
     * Returns true if any depenetration was applied.
     */
    depenetrateShipFromAsteroids(entityId) {
        const entry = this.shipBodies.get(entityId);
        if (entry === undefined)
            return false;
        const body = this.world.getRigidBody(entry.handle);
        if (!body)
            return false;
        let corrected = false;
        for (const [handle, meta] of this.asteroidBodies) {
            const asteroidBody = this.world.getRigidBody(handle);
            if (!asteroidBody)
                continue;
            const shipPos = body.translation();
            const astPos = asteroidBody.translation();
            const dx = shipPos.x - astPos.x;
            const dy = shipPos.y - astPos.y;
            const distSq = dx * dx + dy * dy;
            const minDist = FIGHTER_RADIUS + meta.radius;
            if (distSq >= minDist * minDist)
                continue; // No overlap
            const dist = Math.sqrt(distSq);
            // Compute separation normal (asteroid center -> ship center)
            let nx;
            let ny;
            if (dist < 1e-6) {
                // Degenerate: ship at exact asteroid center. Push in +x by minDist.
                nx = 1;
                ny = 0;
            }
            else {
                nx = dx / dist;
                ny = dy / dist;
            }
            // Push ship to surface of asteroid (position-only, no velocity modification)
            const overlap = minDist - dist;
            body.setTranslation({ x: shipPos.x + nx * overlap, y: shipPos.y + ny * overlap }, true);
            corrected = true;
        }
        return corrected;
    }
    /**
     * Depenetrate a ship from all overlapping celestial bodies (planet + moon).
     * Same pattern as depenetrateShipFromAsteroids: position-only correction,
     * push ship to collision surface along separation normal.
     * Velocity is NOT modified — Rapier handles collision response naturally on the next step.
     * Used after reconciliation as a safety net for moon stuck scenarios.
     * Returns true if any depenetration was applied.
     */
    depenetrateShipFromCelestials(entityId) {
        const entry = this.shipBodies.get(entityId);
        if (entry === undefined)
            return false;
        const body = this.world.getRigidBody(entry.handle);
        if (!body)
            return false;
        let corrected = false;
        for (const ch of this.celestialBodies) {
            const shipPos = body.translation();
            // Use stored positions (ch.x/ch.y) for celestials. With setNextKinematicTranslation(),
            // body.translation() returns the old position until after step().
            const dx = shipPos.x - ch.x;
            const dy = shipPos.y - ch.y;
            const distSq = dx * dx + dy * dy;
            const minDist = FIGHTER_RADIUS + ch.radius;
            if (distSq >= minDist * minDist)
                continue; // No overlap
            const dist = Math.sqrt(distSq);
            // Compute separation normal (celestial center -> ship center)
            let nx;
            let ny;
            if (dist < 1e-6) {
                // Degenerate: ship at exact celestial center. Push in +x by minDist.
                nx = 1;
                ny = 0;
            }
            else {
                nx = dx / dist;
                ny = dy / dist;
            }
            // Push ship to surface of celestial (position-only, no velocity modification)
            const overlap = minDist - dist;
            body.setTranslation({ x: shipPos.x + nx * overlap, y: shipPos.y + ny * overlap }, true);
            corrected = true;
        }
        return corrected;
    }
    /**
     * Toggle ship-asteroid collision for a specific ship.
     * When disabled, the ship passes through asteroids (GROUP_ASTEROID bit cleared from filter).
     * When enabled, the ship collides with asteroids (GROUP_ASTEROID bit set in filter).
     * Note: no longer used during reconciliation replay (Session 142 fix — with 20Hz asteroid
     * sync and setNextKinematicTranslation moon, collision stays enabled for consistent bounces).
     * Kept for potential future use.
     */
    setShipAsteroidCollision(entityId, enabled) {
        const entry = this.shipBodies.get(entityId);
        if (entry === undefined)
            return;
        const body = this.world.getRigidBody(entry.handle);
        if (!body)
            return;
        const collider = body.collider(0);
        if (!collider)
            return;
        const currentGroups = collider.collisionGroups();
        const membership = currentGroups & 0xFFFF0000; // upper 16 bits
        let filter = currentGroups & 0x0000FFFF; // lower 16 bits
        if (enabled) {
            filter |= GROUP_ASTEROID;
        }
        else {
            filter &= ~GROUP_ASTEROID;
        }
        collider.setCollisionGroups(membership | filter);
    }
    /**
     * Snapshot all remote ship body states (position, velocity, rotation).
     * Used to save/restore around reconciliation so extra physics steps
     * don't cause remote ship drift, same pattern as asteroid save/restore.
     * Excludes the local ship (identified by localEntityId).
     */
    snapshotRemoteShipStates(localEntityId) {
        const result = [];
        for (const [entityId, entry] of this.shipBodies) {
            if (entityId === localEntityId)
                continue;
            const body = this.world.getRigidBody(entry.handle);
            if (!body)
                continue;
            const pos = body.translation();
            const vel = body.linvel();
            result.push({ entityId, x: pos.x, y: pos.y, vx: vel.x, vy: vel.y, rotation: body.rotation() });
        }
        return result;
    }
    /**
     * Restore remote ship body states from a snapshot.
     */
    restoreRemoteShipStates(snapshot) {
        for (const s of snapshot) {
            const entry = this.shipBodies.get(s.entityId);
            if (!entry)
                continue;
            const body = this.world.getRigidBody(entry.handle);
            if (!body)
                continue;
            body.setTranslation({ x: s.x, y: s.y }, true);
            body.setLinvel({ x: s.vx, y: s.vy }, true);
            body.setRotation(s.rotation, true);
            body.setAngvel(0, true);
        }
    }
    /**
     * Teleport a ship to a specific state (for server reconciliation).
     */
    resetShipState(entityId, x, y, vx, vy, rotation) {
        const entry = this.shipBodies.get(entityId);
        if (entry === undefined)
            return;
        const body = this.world.getRigidBody(entry.handle);
        if (!body)
            return;
        body.setTranslation({ x, y }, true);
        body.setLinvel({ x: vx, y: vy }, true);
        body.setRotation(rotation, true);
        body.setAngvel(0, true);
    }
    /**
     * Check if a ship exists in the world.
     */
    hasShip(entityId) {
        return this.shipBodies.has(entityId);
    }
    /**
     * Export full debug state of all bodies and colliders in the physics world.
     * Used by debug tooling to compare client vs server physics state.
     */
    debugExport() {
        const bodies = [];
        const colliders = [];
        // Enumerate all rigid bodies
        this.world.bodies.forEach((body) => {
            let type;
            if (body.isFixed()) {
                type = 'fixed';
            }
            else if (body.isDynamic()) {
                type = 'dynamic';
            }
            else {
                type = 'kinematic';
            }
            const pos = body.translation();
            const vel = body.linvel();
            bodies.push({
                type,
                x: pos.x,
                y: pos.y,
                vx: vel.x,
                vy: vel.y,
                rotation: body.rotation(),
            });
        });
        // Enumerate all colliders
        this.world.colliders.forEach((collider) => {
            const pos = collider.translation();
            const rot = collider.rotation();
            const groups = collider.collisionGroups();
            const shapeType = collider.shapeType();
            const entry = shapeType === RAPIER.ShapeType.Ball
                ? {
                    shape: 'ball',
                    x: pos.x,
                    y: pos.y,
                    rotation: rot,
                    collisionGroup: groups,
                    radius: collider.radius(),
                }
                : {
                    shape: 'cuboid',
                    x: pos.x,
                    y: pos.y,
                    rotation: rot,
                    collisionGroup: groups,
                    halfExtentsX: collider.halfExtents().x,
                    halfExtentsY: collider.halfExtents().y,
                };
            colliders.push(entry);
        });
        // Tracked ship metadata
        const ships = [];
        for (const [entityId, entry] of this.shipBodies) {
            const body = this.world.getRigidBody(entry.handle);
            if (!body)
                continue;
            const pos = body.translation();
            const vel = body.linvel();
            ships.push({ entityId, x: pos.x, y: pos.y, vx: vel.x, vy: vel.y, rotation: body.rotation() });
        }
        // Tracked asteroid metadata
        const asteroids = [];
        for (const [handle, meta] of this.asteroidBodies) {
            const body = this.world.getRigidBody(handle);
            if (!body)
                continue;
            const pos = body.translation();
            const vel = body.linvel();
            asteroids.push({ x: pos.x, y: pos.y, vx: vel.x, vy: vel.y, radius: meta.radius, mass: meta.mass });
        }
        // Tracked celestial metadata
        const celestials = [];
        for (let i = 0; i < this.celestialBodies.length; i++) {
            const ch = this.celestialBodies[i];
            const body = this.world.getRigidBody(ch.bodyHandle);
            if (!body)
                continue;
            const pos = body.translation();
            celestials.push({ index: i, x: pos.x, y: pos.y, radius: ch.radius, mass: ch.mass });
        }
        return { bodies, colliders, ships, asteroids, celestials };
    }
    /**
     * Destroy the Rapier world and free memory.
     */
    destroy() {
        this.world.free();
        this.shipBodies.clear();
        this.celestialBodies = [];
        this.asteroidBodies.clear();
    }
}
//# sourceMappingURL=rapierWorld.js.map